import type { RequestFn } from '../http';
import type { JibbleSettings } from './jibble';

export interface OnCallUserRef {
  id: string;
  displayName: string;
  email: string;
  phoneMobile: string | null;
}

export interface OnCallConfig {
  enabled: boolean;
  rotationUserIds: string[];
  fallbackUserId: string | null;
  fallbackHoursBeforeStart: number;
  handoverWeekday: number;
  handoverHour: number;
  maxConsecutiveWeeks: number;
  minPauseWeeks: number;
  serviceWindow: {
    timeZone: string;
    days: { from: string; to: string }[][];
    holidayRegion: string;
  };
  alertThreshold: number;
  portalEmergencyAlwaysAlerts: boolean;
  escalateToBackupMinutes: number;
  escalateToLeadMinutes: number;
  leadUserIds: string[];
  stipendCents: number;
  minDeploymentMinutes: number;
  nightSurchargePct: number;
  nightFromHour: number;
  nightToHour: number;
  sundaySurchargePct: number;
  holidaySurchargePct: number;
  smsEnabled: boolean;
  smsTestMode: boolean;
  smsSenderId: string;
  smsMaxPerHour: number;
  maxAutoAssessmentsPerHour: number;
}

export interface OnCallShift {
  id: string;
  startsAt: string;
  endsAt: string;
  primaryUserId: string | null;
  backupUserId: string | null;
  status: 'OPEN' | 'PLANNED' | 'CANCELLED';
  coverageSource: string | null;
  stipendCents: number;
  note: string | null;
  primaryUser: OnCallUserRef | null;
  backupUser: OnCallUserRef | null;
  _count?: { alerts: number; deployments: number };
}

export interface RotationWarning {
  index: number;
  message: string;
}

export interface PlannedShiftPreview {
  startsAt: string;
  endsAt: string;
  primaryUserId: string | null;
  backupUserId: string | null;
  stipendCents: number;
  primaryName: string;
  backupName: string | null;
  primaryHasMobile: boolean;
}

export interface EmergencyAssessment {
  id: string;
  ticketSource: 'CW' | 'WECLAPP';
  ticketId: string;
  score: number;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  alerted: boolean;
  headline: string;
  reasoning: string;
  reasons: string[];
  recommendedAction: string | null;
  trigger: 'PORTAL_EMERGENCY' | 'AUTO_SCAN' | 'MANUAL';
  skipReason: string | null;
  createdAt: string;
}

export interface SmsLogEntry {
  id: string;
  recipient: string;
  text: string;
  status: 'SENT' | 'FAILED' | 'SKIPPED';
  providerCode: string | null;
  priceMilli: number | null;
  parts: number | null;
  error: string | null;
  createdAt: string;
  user: { id: string; displayName: string } | null;
}

export interface EmergencyAlert {
  id: string;
  ticketSource: 'CW' | 'WECLAPP';
  ticketId: string;
  status: 'PENDING' | 'ACKNOWLEDGED' | 'EXHAUSTED' | 'CANCELLED';
  stage: number;
  summary: string;
  customerName: string | null;
  acknowledgedAt: string | null;
  createdAt: string;
  assessment: EmergencyAssessment;
  acknowledgedByUser: { id: string; displayName: string } | null;
  shift: OnCallShift | null;
  messages: SmsLogEntry[];
  deployment: OnCallDeployment | null;
}

export interface OnCallDeployment {
  id: string;
  userId: string;
  shiftId: string | null;
  alertId: string | null;
  ticketSource: string | null;
  ticketId: string | null;
  startedAt: string;
  autoStartedAt: string | null;
  endedAt: string | null;
  rawMinutes: number | null;
  billedMinutes: number | null;
  surchargeKind: 'STANDARD' | 'NIGHT' | 'SUNDAY' | 'HOLIDAY' | null;
  surchargePct: number | null;
  jibbleInEntryId: string | null;
  jibbleOutEntryId: string | null;
  jibbleError: string | null;
  workDescription: string | null;
  emergencyJustification: string | null;
  billingStatus: 'PENDING' | 'BOOKED' | 'QUEUED' | 'FAILED' | 'MANUAL' | null;
  billingError: string | null;
  note: string | null;
}

export interface FinishDeploymentInput {
  workDescription: string;
  emergencyJustification: string;
  /** Korrigierter Beginn als ISO-Zeitpunkt. */
  startedAt?: string;
  note?: string;
}

export interface PayoutRow {
  userId: string;
  displayName: string;
  weeks: number;
  stipendCents: number;
  deploymentCount: number;
  billedMinutes: number;
  minutesByKind: Record<'STANDARD' | 'NIGHT' | 'SUNDAY' | 'HOLIDAY', number>;
  surchargeMinutes: number;
}

export interface OnCallCurrent {
  enabled: boolean;
  handover: { weekday: number; weekdayLabel: string; hour: number };
  stipendCents: number;
  openWeeks: OnCallShift[];
  canClaim: boolean;
  fallbackName: string | null;
  current: {
    id: string;
    startsAt: string;
    endsAt: string;
    primary: OnCallUserRef | null;
    backup: OnCallUserRef | null;
    isMe: boolean;
  } | null;
  myUpcoming: {
    id: string;
    startsAt: string;
    endsAt: string;
    backup: OnCallUserRef | null;
    stipendCents: number;
  }[];
}

export function createOnCallApi(request: RequestFn) {
  return {
    // ── Für alle ──────────────────────────────────────────────────────────
    async getOnCallCurrent() {
      return request<OnCallCurrent>('/on-call/current');
    },
    async claimOnCallShift(id: string) {
      return request<{ shift: OnCallShift }>(`/on-call/shifts/${id}/claim`, { method: 'POST' });
    },
    async releaseOnCallShift(id: string) {
      return request<{ shift: OnCallShift }>(`/on-call/shifts/${id}/release`, { method: 'POST' });
    },
    async getMyDeployments() {
      return request<{ deployments: OnCallDeployment[] }>('/on-call/my-deployments');
    },
    async finishMyDeployment(id: string, input: FinishDeploymentInput) {
      return request<{
        deployment: OnCallDeployment;
        billing: { status: string; error: string | null };
      }>(`/on-call/deployments/${id}/finish`, { method: 'POST', body: JSON.stringify(input) });
    },
    /** Mindestlängen der Pflichtdokumentation — die Oberfläche prüft dieselben Werte. */
    async getDocumentationRules() {
      return request<{ minWorkDescription: number; minJustification: number }>(
        '/on-call/documentation-rules'
      );
    },
    /** Admin: gescheiterte Buchung eines Einsatzes erneut versuchen. */
    async rebillDeployment(id: string) {
      return request<{ status: string; error: string | null }>(
        `/on-call/deployments/${id}/rebill`, { method: 'POST' }
      );
    },

    // ── Admin: Einstellungen ──────────────────────────────────────────────
    async getOnCallConfig() {
      return request<{
        config: OnCallConfig;
        jibble: JibbleSettings;
        sevenApiKeySet: boolean;
        weekdayLabels: string[];
      }>('/admin/on-call/config');
    },
    async saveOnCallConfig(patch: Partial<OnCallConfig>) {
      return request<{ config: OnCallConfig }>('/admin/on-call/config', {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
    },
    async saveSevenApiKey(apiKey: string | null) {
      return request<{ ok: true }>('/admin/on-call/seven-key', {
        method: 'PUT',
        body: JSON.stringify({ apiKey }),
      });
    },
    async testSevenConnection() {
      return request<{ ok: boolean; balance: number | null; error: string | null }>(
        '/admin/on-call/seven-test', { method: 'POST' }
      );
    },
    async sendTestSms(to: string, text?: string) {
      return request<{ status: string; reason: string | null }>('/admin/on-call/sms-test', {
        method: 'POST',
        body: JSON.stringify({ to, text }),
      });
    },

    // ── Admin: Plan ───────────────────────────────────────────────────────
    async getOnCallShifts(params?: { from?: string; to?: string }) {
      const q = new URLSearchParams();
      if (params?.from) q.set('from', params.from);
      if (params?.to) q.set('to', params.to);
      const suffix = q.toString() ? `?${q}` : '';
      return request<{ shifts: OnCallShift[]; warnings: RotationWarning[] }>(`/admin/on-call/shifts${suffix}`);
    },
    async previewRotation(weeks: number, from?: string) {
      return request<{ shifts: PlannedShiftPreview[]; warnings: RotationWarning[] }>(
        '/admin/on-call/plan-preview',
        { method: 'POST', body: JSON.stringify({ weeks, from }) }
      );
    },
    async applyRotation(weeks: number, from?: string) {
      return request<{ created: number; skipped: number; warnings: RotationWarning[] }>(
        '/admin/on-call/plan-apply',
        { method: 'POST', body: JSON.stringify({ weeks, from }) }
      );
    },
    async createShift(body: {
      startsAt: string; endsAt: string; primaryUserId?: string;
      backupUserId?: string | null; stipendCents?: number; note?: string | null;
    }) {
      return request<{ shift: OnCallShift }>('/admin/on-call/shifts', {
        method: 'POST', body: JSON.stringify(body),
      });
    },
    async updateShift(id: string, body: Partial<{
      startsAt: string; endsAt: string; primaryUserId: string;
      backupUserId: string | null; stipendCents: number; note: string | null;
      status: 'OPEN' | 'PLANNED' | 'CANCELLED';
    }>) {
      return request<{ shift: OnCallShift }>(`/admin/on-call/shifts/${id}`, {
        method: 'PATCH', body: JSON.stringify(body),
      });
    },

    // ── Admin: Alarme & Abrechnung ────────────────────────────────────────
    async getEmergencyAlerts(limit = 50) {
      return request<{ alerts: EmergencyAlert[] }>(`/admin/on-call/alerts?limit=${limit}`);
    },
    async getEmergencyAssessments(limit = 50) {
      return request<{ assessments: EmergencyAssessment[] }>(`/admin/on-call/assessments?limit=${limit}`);
    },
    async assessTicket(body: {
      ticketId: string; source: 'CW' | 'WECLAPP';
      alert?: boolean; ignoreServiceWindow?: boolean;
    }) {
      return request<
        | { assessment: EmergencyAssessment; alerted: false }
        | { alerted: true; alertId: string; score: number; recipients: string[] }
        | { alerted: false; reason: string; score: number | null }
      >('/admin/on-call/assess', { method: 'POST', body: JSON.stringify(body) });
    },
    /** `month` als „YYYY-MM" — die Monatsgrenzen zieht der Server in der Zeitzone der Servicezeit. */
    async getPayout(month: string) {
      return request<{ from: string; to: string; rows: PayoutRow[]; rates: Record<string, number> }>(
        `/admin/on-call/payout?month=${encodeURIComponent(month)}`
      );
    },
    async getSmsLog(limit = 100) {
      return request<{ messages: SmsLogEntry[] }>(`/admin/on-call/sms-log?limit=${limit}`);
    },
  };
}
